import { useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';

// =============================================================================
// Editor de texto com marcações de emoção do Fish s2.1-pro.
// Compartilhado pelo Chat CRM (modal de voz) e por Campanhas (tipo "Voz
// clonada") — a paleta precisa ser a MESMA nos dois lugares.
//
// O modelo lê instruções em linguagem natural entre colchetes — [excited],
// [whispers sweetly] — e não uma lista fechada de códigos. Duas consequências
// que moldam esta UI:
//
//  1. A marcação vale DO PONTO ONDE ESTÁ ATÉ a próxima marcação ou o fim do
//     texto. Para aplicar a emoção só no trecho selecionado, é preciso fechar
//     com [neutral] logo depois — é o que applyTag faz.
//  2. As tags são interpretadas em inglês (é assim que o modelo foi treinado),
//     então o rótulo é PT-BR e o que viaja para a API é o termo em inglês.
//
// Ficaram de fora os marcadores de parênteses do Fish ((break), (laugh)…):
// eles exigem `normalize: false`, o que desliga a expansão de números e moedas
// e faria "R$ 49,90" sair errado em português. Pausa e riso têm equivalente em
// colchete, que funciona com a normalização ligada.
// =============================================================================

export type EmotionTag = {
  label: string;   // o que o usuário vê
  tag: string;     // o que vai para o Fish
  hint: string;
  scoped: boolean; // true = vale para um trecho · false = acontece num ponto
};

export const EMOTION_GROUPS: { title: string; color: string; items: EmotionTag[] }[] = [
  {
    title: 'Emoção',
    color: 'violet',
    items: [
      { label: 'Empolgado', tag: 'excited', hint: 'Energia e entusiasmo', scoped: true },
      { label: 'Feliz', tag: 'happy', hint: 'Leve e sorridente', scoped: true },
      { label: 'Caloroso', tag: 'with gentle warmth', hint: 'Acolhedor, próximo', scoped: true },
      { label: 'Confiante', tag: 'confident', hint: 'Firme e seguro', scoped: true },
      { label: 'Curioso', tag: 'curious', hint: 'Interessado, pergunta', scoped: true },
      { label: 'Preocupado', tag: 'worried', hint: 'Tom de cuidado', scoped: true },
      { label: 'Triste', tag: 'sad', hint: 'Melancólico', scoped: true },
      { label: 'Surpreso', tag: 'surprised', hint: 'Espanto', scoped: true },
      { label: 'Bravo', tag: 'angry', hint: 'Irritado, forte', scoped: true },
      { label: 'Pedindo desculpas', tag: 'apologetic', hint: 'Tom de desculpa', scoped: true },
    ],
  },
  {
    title: 'Jeito de falar',
    color: 'blue',
    items: [
      { label: 'Sussurrando', tag: 'whisper', hint: 'Volume baixo, íntimo', scoped: true },
      { label: 'Enfático', tag: 'emphasis', hint: 'Destaca as palavras', scoped: true },
      { label: 'Devagar', tag: 'speaking slowly', hint: 'Ritmo mais lento', scoped: true },
      { label: 'Rápido', tag: 'speaking quickly', hint: 'Ritmo acelerado', scoped: true },
      { label: 'Como um segredo', tag: 'as if confiding a secret', hint: 'Confidencial', scoped: true },
      { label: 'Simpático', tag: 'friendly and persuasive', hint: 'Tom comercial simpático', scoped: true },
      { label: 'Sério', tag: 'serious', hint: 'Formal e direto', scoped: true },
      { label: 'Gritando', tag: 'shouting', hint: 'Voz alta', scoped: true },
      { label: 'Neutro', tag: 'neutral', hint: 'Volta ao normal daqui em diante', scoped: false },
    ],
  },
  {
    title: 'Sons e pausas',
    color: 'amber',
    items: [
      { label: 'Risada', tag: 'laughs', hint: 'Ri neste ponto', scoped: false },
      { label: 'Risadinha', tag: 'chuckles', hint: 'Riso curto', scoped: false },
      { label: 'Suspiro', tag: 'sigh', hint: 'Suspira', scoped: false },
      { label: 'Susto', tag: 'gasp', hint: 'Inspiração de surpresa', scoped: false },
      { label: 'Respira', tag: 'inhale', hint: 'Respiração audível', scoped: false },
      { label: 'Pausa', tag: 'pause', hint: 'Silêncio curto', scoped: false },
      { label: 'Pausa longa', tag: 'long pause', hint: 'Silêncio maior', scoped: false },
    ],
  },
];

export const TAG_PATTERN = /\[[^[\]\n]{1,60}\]/g;

export function countVoiceTags(text: string) {
  return (String(text || '').match(TAG_PATTERN) || []).length;
}

export function stripVoiceTags(text: string) {
  return String(text || '').replace(TAG_PATTERN, '').replace(/[ \t]{2,}/g, ' ').trim();
}

const CHIP_STYLES: Record<string, string> = {
  violet: 'border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100',
  blue: 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100',
  amber: 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100',
};

interface Props {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  rows?: number;
  placeholder?: string;
  label?: string;
  // Variáveis de contato ({{nome}}, {{primeironome}}…) — só Campanhas usa.
  variables?: { label: string; token: string }[];
  // Linha de rodapé sob o textarea (contagem de bytes, custo…).
  footer?: React.ReactNode;
}

export function VoiceEmotionEditor({ value, onChange, disabled, rows = 5, placeholder, label = 'Texto', variables, footer }: Props) {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Última seleção conhecida do textarea. Guardada num ref porque o clique no
  // chip pode roubar o foco antes de lermos selectionStart/End.
  const selectionRef = useRef<{ start: number; end: number }>({ start: 0, end: 0 });
  const [hasSelection, setHasSelection] = useState(false);

  const rememberSelection = () => {
    const el = textareaRef.current;
    if (!el) return;
    selectionRef.current = { start: el.selectionStart ?? 0, end: el.selectionEnd ?? 0 };
    setHasSelection(el.selectionEnd > el.selectionStart);
  };

  const commit = (next: string, caret: number) => {
    onChange(next);
    selectionRef.current = { start: caret, end: caret };
    setHasSelection(false);
    requestAnimationFrame(() => {
      const el = textareaRef.current;
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    });
  };

  // Insere a marcação. Para uma emoção com trecho selecionado, fecha com
  // [neutral] logo depois — sem isso, o Fish leva a emoção até o fim do texto.
  const applyTag = (item: EmotionTag) => {
    const { start, end } = selectionRef.current;
    const before = value.slice(0, start);
    const selected = value.slice(start, end);
    const after = value.slice(end);
    const spaceBefore = before && !/\s$/.test(before) ? ' ' : '';

    if (item.scoped && selected.trim()) {
      const rest = after.trimStart();
      // Não empilha [neutral] se o trecho já termina o texto ou já é seguido
      // por outra marcação, que por si só encerra a anterior.
      const needsReset = rest.length > 0 && !rest.startsWith('[');
      const opening = `${spaceBefore}[${item.tag}] `;
      const closing = needsReset ? ' [neutral]' : '';
      commit(
        `${before}${opening}${selected}${closing}${after}`,
        before.length + opening.length + selected.length + closing.length,
      );
      return;
    }

    const marker = `${spaceBefore}[${item.tag}] `;
    commit(`${before}${marker}${selected}${after}`, before.length + marker.length + selected.length);
  };

  const insertVariable = (token: string) => {
    const { start, end } = selectionRef.current;
    const before = value.slice(0, start);
    const after = value.slice(end);
    commit(`${before}${token}${after}`, before.length + token.length);
  };

  const tagCount = countVoiceTags(value);
  const keepFocus = (event: ReactMouseEvent) => event.preventDefault();

  return (
    <div className="space-y-2">
      <label className="block">
        {label && <span className="text-xs font-medium text-slate-600">{label}</span>}
        <textarea
          ref={textareaRef}
          value={value}
          onChange={(event) => { onChange(event.target.value); rememberSelection(); }}
          onSelect={rememberSelection}
          onKeyUp={rememberSelection}
          onMouseUp={rememberSelection}
          onBlur={rememberSelection}
          disabled={disabled}
          rows={rows}
          className="mt-1 w-full resize-none rounded-md border border-slate-300 px-3 py-2 text-sm disabled:bg-slate-100"
          placeholder={placeholder || 'Texto que será falado na voz clonada'}
        />
        {footer}
      </label>

      {variables && variables.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-2.5">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Variáveis do contato</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {variables.map((variable) => (
              <button
                key={variable.token}
                type="button"
                onMouseDown={keepFocus}
                onClick={() => insertVariable(variable.token)}
                disabled={disabled}
                title={`Insere ${variable.token}, trocado pelo dado do contato antes de gerar o áudio`}
                className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
              >
                {variable.label}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs font-semibold text-slate-700">🎭 Emoção da voz</p>
          {tagCount > 0 && (
            <button
              type="button"
              onClick={() => onChange(stripVoiceTags(value))}
              disabled={disabled}
              className="text-[11px] font-medium text-slate-500 underline hover:text-slate-800 disabled:opacity-40"
            >
              Limpar marcações
            </button>
          )}
        </div>
        <p className="mt-1 text-[11px] leading-relaxed text-slate-500">
          {hasSelection
            ? 'Trecho selecionado — clique numa emoção para aplicar só nele.'
            : 'Selecione um trecho do texto e clique numa emoção para aplicá-la só nesse pedaço. Sem seleção, a marcação entra no cursor e vale até a próxima.'}
        </p>

        <div className="mt-2 space-y-2">
          {EMOTION_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{group.title}</p>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {group.items.map((item) => (
                  <button
                    key={item.tag}
                    type="button"
                    // preventDefault mantém o foco (e a seleção) no textarea.
                    onMouseDown={keepFocus}
                    onClick={() => applyTag(item)}
                    disabled={disabled || !value.trim()}
                    title={`${item.hint} — envia [${item.tag}] para o modelo`}
                    className={`rounded-full border px-2.5 py-1 text-[11px] font-medium disabled:opacity-40 ${CHIP_STYLES[group.color]}`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
